import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Calendar, Receipt, ShieldCheck, MessageCircle, Sparkles } from "lucide-react";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import PageMeta from "../components/PageMeta";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Welcome back!");
      if (data?.role === "super_admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fillDemo = () => {
    setEmail("doctor@demo.com");
    setPassword("demo123");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-sky-50 px-4 py-10">
      <PageMeta
        title="Log in to HeDesk — Clinic Dashboard Sign In"
        description="Sign in to HeDesk to manage your clinic's appointments, patient records, prescriptions and billing. Built for Indian doctors and receptionists."
      />
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">
        {/* Left column — Auth form */}
        <div className="w-full max-w-md mx-auto">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="login-logo">
          <img
            src="/logo.jpeg"
            alt="HeDesk"
            className="w-10 h-10 rounded-[22%] object-cover"
          />
          <span className="font-bold text-2xl text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
            Welcome back
          </h1>
          <p className="text-sm text-slate-600 mb-6">Sign in to your clinic dashboard.</p>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 mb-4 border-slate-300"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="doctor@example.com"
                data-testid="login-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-1.5 h-11"
              />
            </div>

            {error && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center justify-between mt-4">
            {/*<button
              type="button"
              onClick={fillDemo}
              className="text-sm text-blue-600 hover:underline"
              data-testid="fill-demo-btn"
            >
              Use demo doctor
            </button>*/}
            <Link
              to="/forgot-password"
              className="text-sm text-slate-600 hover:text-blue-600 hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <p className="text-center text-sm text-slate-600 mt-6">
          Don't have a clinic yet?{" "}
          <Link to="/signup" className="text-blue-600 font-medium hover:underline" data-testid="login-signup-link">
            Start free trial
          </Link>
        </p>
        </div>

        {/* Right column — SEO content (hidden on mobile; still in DOM for crawlers via lg:block) */}
        <aside className="hidden lg:block max-w-md" aria-label="HeDesk features and trust">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Built for Indian clinics
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-3 leading-tight" style={{ fontFamily: "Manrope" }}>
            Run your whole clinic from one clean dashboard.
          </h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            HeDesk is the simplest way for Indian doctors and receptionists to manage appointments, walk-in queues, prescriptions, and billing — all in one place. Sign in to your account to pick up exactly where you left off, with all your patient records, schedules, and pending invoices ready in seconds.
          </p>
          <ul className="space-y-4">
            <li className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Smart scheduling & queue</div>
                <div className="text-sm text-slate-600">Online booking, walk-in tokens, and a live "Now Serving" board for every doctor.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Billing in one click</div>
                <div className="text-sm text-slate-600">Generate, share, and mark invoices paid via Cash, UPI, Card, or Bank Transfer.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">WhatsApp reminders</div>
                <div className="text-sm text-slate-600">Send appointment confirmations and invoice links instantly — no SMS fees.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Private & secure</div>
                <div className="text-sm text-slate-600">Patient data is encrypted in transit, role-based access, and audit-logged.</div>
              </div>
            </li>
          </ul>
          <div className="mt-6 p-4 rounded-xl bg-white border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Trusted by clinics across India</div>
            <div className="text-sm text-slate-700">
              From a 1-doctor general practice to multi-specialty centres — HeDesk works on any browser, on any device, without installs.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
